import { skyTerrainHeight } from './87-sky-terrain.mjs';
import {
  SKY_WORLD,
  SKY_WORLD_OBSTACLE_RADII,
  skyObstacleDimensions,
} from './86-sky-world-scale.mjs';
import { skyEquipment } from './77-sky-upgrades.mjs';
import { SKY_STAGES } from './76-sky-campaign.mjs';

import {
  SKY_BONUSES as BONUSES,
  skyRewardActive as active,
  stepSkyRewardClocks,
} from './107-sky-rewards.mjs';

/** Derive temporary equipment without modifying purchased run equipment.
 * Durations are simulation seconds; movement remains bounded in metres/second. */
export function skyEffectiveEquipment(state) {
  const equipment = skyEquipment(state?.upgrades);
  const supercharge = state?.bossSupercharge;
  const chargeActive =
    supercharge != null &&
    supercharge.sector === state?.sector &&
    supercharge.status === 'active' &&
    supercharge.remaining > 0;
  equipment.shotDamage *= Math.max(1, active(state, 'power') ? 1.35 : 1, chargeActive ? 1.5 : 1);
  if (active(state, 'rapid')) equipment.shotInterval *= 0.7;
  if (active(state, 'boost')) equipment.lateralSpeed = Math.min(24, equipment.lateralSpeed * 1.2);
  if (active(state, 'focus')) {
    equipment.aimCapture += 0.5;
    equipment.aimStrength = Math.min(0.85, equipment.aimStrength + 0.13);
  }
  return equipment;
}

/** Cannon cadence in active seconds. Choose the strongest temporary reduction
 * relative to the same purchased equipment; rapid and overdrive never multiply.
 * Shared by physical emission and its short visible muzzle flash. */
export function skyCannonInterval(state, equipment = skyEffectiveEquipment(state)) {
  return Math.min(
    equipment.shotInterval,
    skyEquipment(state.upgrades).shotInterval * (state.overdriveTime > 0 ? 0.5 : 1),
  );
}

/** Aegis absorbs damage for its remaining simulation duration; it never repairs
 * permanent shield capacity or spends a purchased upgrade. */
export function skyAbsorbBonusHit(state) {
  return active(state, 'shield');
}

/** Advance reward sources only while authored combat is available. Optional
 * opportunity is a pure test/adapter input; omitted uses the shared projection. */
export function stepSkyBonuses(state, dt, opportunity) {
  stepSkyRewardClocks(state, dt, opportunity);
}

/** Stage/death boundaries expire effects. Settled pickup identities, salvage
 * and purchased gear survive; only an explicit new run resets those ledgers. */
export function clearSkyBonuses(state) {
  state.bonuses = {};
  state.waveReward = null;
  state.bossSupercharge = null;
}

/** Read-only, stable ordering for countdown telemetry; remaining is seconds. */
export function skyBonusStatus(state) {
  const wave = state.waveReward;
  const entries = Object.entries(BONUSES)
    .filter(([key]) => active(state, key) || (wave?.key === key && wave.remaining > 0))
    .map(([key, bonus]) => ({
      key,
      ...bonus,
      remaining: Math.max(state.bonuses?.[key] ?? 0, wave?.key === key ? wave.remaining : 0),
      queued: wave?.key === key && !wave.active && !(state.bonuses?.[key] > 0),
    }));
  const charge = state.bossSupercharge;
  if (charge != null && charge.sector === state.sector && charge.status !== 'spent')
    entries.push({
      key: 'supercharge',
      label: 'BOSS SUPERCHARGE',
      duration: charge.duration,
      remaining: charge.remaining,
      queued: charge.status === 'ready',
    });
  return entries;
}

/** Create at most two harmless ground megafauna per combat stage. Seeded choices
 * are replayable; later stages widen the reward pool without changing purchases.
 * A 108m horizon arrival lies inside the renderer's 155m fog limit. At 36% rail
 * speed, even chapter four's late arrival reaches the 48m interaction region
 * before the 39s guardian transition, leaving at least four seconds nearby. */
export function createSkyBonusTarget(state, chapterIndex) {
  if (![1, 4].includes(chapterIndex)) return null;
  const key = `${state.sector}:${chapterIndex}`;
  state.bonusSpawnKeys ??= [];
  if (state.bonusSpawnKeys.includes(key)) return null;
  state.bonusSpawnKeys.push(key);
  const pool = ['rapid', 'power', 'shield'];
  if (state.sector >= 3) pool.push('boost');
  if (state.sector >= 8) pool.push('focus');
  let hash =
    (state.seed ^ Math.imul(state.sector + 1, 374761393) ^ Math.imul(chapterIndex, 668265263)) >>>
    0;
  hash = Math.imul(hash ^ (hash >>> 13), 1274126177) >>> 0;
  hash ^= hash >>> 16;
  hash >>>= 0;
  const x = (hash & 1 ? -1 : 1) * (6 + ((hash >>> 4) % 21) / 10);
  const groundY = Number.isFinite(state.terrainTravel)
    ? skyTerrainHeight(x, -108, state.terrainTravel)
    : 0;
  return {
    id: state.nextId++,
    kind: 'bonus',
    bonusForm: ['forest', 'fungi', 'ice'].includes(SKY_STAGES[state.sector]?.biome)
      ? 'mammoth'
      : 'mechwalker',
    bonusKind: pool[hash % pool.length],
    bonusClaimed: false,
    // Fixed stage toughness rewards committed tracking; purchased firepower
    // is never cancelled by scaling health to the player’s equipment.
    hp: 28 + Math.min(17, Math.max(0, state.sector)),
    x,
    baseX: x,
    y: 2.3 + groundY,
    groundY,
    terrainTravel: state.terrainTravel,
    z: -108,
    age: 0,
    radius: 1.8,
    speedScale: 0.36,
    aggression: 0,
    value: 100,
  };
}

/** Advance a harmless ground creature's slow wandering path in metres. The
 * caller advances age and rail depth first; subtracting the previous depth sway
 * avoids frame-rate-dependent drift. No movement runs while the game is paused. */
export function stepSkyBonusTarget(target, dt) {
  if (target.kind !== 'bonus' || !Number.isFinite(dt) || dt <= 0) return;
  const age = target.age ?? 0;
  const phase = ((target.id ?? 0) % 7) * 0.7;
  target.baseX ??= target.x;
  const sway = (time) => Math.sin(time * 0.48 + phase);
  const x = target.baseX + (sway(age) - sway(0)) * 0.7;
  target.vx = (x - target.x) / dt;
  target.x = x;
  target.vy = 0;
  const depth = Math.sin(age * 0.8 + phase) * 0.45;
  target.z += depth - (target.walkDepth ?? Math.sin(phase) * 0.45);
  target.walkDepth = depth;
}

/** Claim once on destruction. Repeated rewards refresh rather than stack their
 * duration; different kinds can coexist. Invalid targets fail without mutation. */
export function claimSkyBonus(state, target) {
  if (
    target?.kind !== 'bonus' ||
    !Number.isFinite(target.hp) ||
    target.hp > 0 ||
    target.bonusClaimed ||
    !Object.hasOwn(BONUSES, target.bonusKind)
  )
    return null;
  state.bonusClaimIds ??= [];
  if (Number.isSafeInteger(target.id)) {
    if (state.bonusClaimIds.includes(target.id)) return null;
    state.bonusClaimIds.push(target.id);
  }
  target.bonusClaimed = true;
  const key = target.bonusKind,
    bonus = BONUSES[key];
  state.bonuses ??= {};
  state.bonuses[key] = bonus.duration;
  state.bonusesCollected = (state.bonusesCollected ?? 0) + 1;
  return { key, ...bonus };
}

/** Seeded destructible ground formations, in metres and hit points. Two staggered
 * arrivals alternate inner/outer lanes, leaving a central passage and open sky.
 * Existing chapter dispatch owns once-only spawning; invalid inputs emit nothing. */
export function createSkyObstacles(state, chapterIndex) {
  if (!Number.isInteger(chapterIndex) || chapterIndex < 0 || chapterIndex > 5) return [];
  const hash =
    (Math.imul((state.seed ?? 1) ^ (state.sector + 1), 374761393) ^
      Math.imul(chapterIndex + 1, 668265263)) >>>
    0;
  const count = chapterIndex % 2 === 0 ? 2 : 1;
  return Array.from({ length: count }, (_, index) => {
    const tier = (hash + index + chapterIndex) % 3;
    const radius = SKY_WORLD_OBSTACLE_RADII[tier];
    const obstacleForm = (hash + index) % 2 ? 'rock' : 'ruin';
    const dimensions = skyObstacleDimensions(obstacleForm, radius);
    const hp = [4, 8, 12][tier];
    const x =
      (index % 2 === (hash & 1) ? 1 : -1) *
      ((chapterIndex + index) % 2 ? 3.4 + ((hash >>> 5) % 15) / 10 : 6 + ((hash >>> 5) % 26) / 10);
    const z = -145 - index * 24;
    const groundY = Number.isFinite(state.terrainTravel)
      ? skyTerrainHeight(x, z, state.terrainTravel)
      : SKY_WORLD.groundY;
    return {
      id: state.nextId++,
      kind: 'pillar',
      obstacleForm,
      hp,
      maxHp: hp,
      radius,
      x,
      y: groundY + dimensions.height / 2,
      z,
      groundY,
      terrainTravel: state.terrainTravel,
      age: 0,
      speedScale: 0.7,
      aggression: 0,
      value: [15, 25, 40][tier],
    };
  });
}
